/** Maps backend status strings to a StatusBadge tone + Russian label.
 *  State is never colour-only — the label word always travels with the tone. An unknown status
 *  keeps its slug as the word (and `known: false`, so a page can show it quietly in ink-3). */

export type Tone = 'ok' | 'warn' | 'danger' | 'neutral' | 'live'
export interface StatusView {
  tone: Tone
  label: string
  /** false: the backend sent a status this table does not know — the slug is the label */
  known?: boolean
}

type Table = Record<string, { tone: Tone; label: string }>

function lookup(table: Table, status: string): StatusView {
  const hit = table[status]
  return hit ? { ...hit, known: true } : { tone: 'neutral', label: status, known: false }
}

const JOB: Table = {
  pending: { tone: 'neutral', label: 'Ожидает' },
  moderating: { tone: 'warn', label: 'Модерация' },
  queued: { tone: 'warn', label: 'В очереди' },
  submitted: { tone: 'live', label: 'Отправлена' },
  processing: { tone: 'live', label: 'Генерируется' },
  succeeded: { tone: 'ok', label: 'Готово' },
  failed: { tone: 'danger', label: 'Ошибка' },
  cancelled: { tone: 'neutral', label: 'Отменена' },
  refunded: { tone: 'neutral', label: 'Возврат' },
}

const PAYMENT: Table = {
  created: { tone: 'neutral', label: 'Создан' },
  pending: { tone: 'warn', label: 'Ожидает' },
  paid: { tone: 'ok', label: 'Оплачен' },
  failed: { tone: 'danger', label: 'Ошибка' },
  expired: { tone: 'neutral', label: 'Истёк' },
  refunded: { tone: 'neutral', label: 'Возврат' },
  partially_paid: { tone: 'warn', label: 'Недоплата' },
  partially_refunded: { tone: 'warn', label: 'Частичный возврат' },
}

/** PayoutStatus (src/core/models): pending → reviewing → approved → processing → completed,
 *  or rejected / failed. */
export const PAYOUT: Table = {
  pending: { tone: 'warn', label: 'Новая' },
  reviewing: { tone: 'warn', label: 'На проверке' },
  approved: { tone: 'ok', label: 'Одобрена' },
  processing: { tone: 'live', label: 'Отправляется' },
  completed: { tone: 'ok', label: 'Выплачена' },
  rejected: { tone: 'neutral', label: 'Отклонена' },
  failed: { tone: 'danger', label: 'Сбой у провайдера' },
}

/** A partner's referral earning (EarningStatus): held until it matures (or the antifraud
 *  clears it), then available to withdraw, then paid out; cancelled on a refund. «Зарезервировано»
 *  is the word the bot and the Mini App use for `held` (glossary WP-22). */
export const EARNING: Table = {
  held: { tone: 'warn', label: 'Зарезервировано' },
  available: { tone: 'ok', label: 'Доступно' },
  paid: { tone: 'neutral', label: 'Выплачено' },
  cancelled: { tone: 'neutral', label: 'Отменено' },
}

/** Support tickets: open (the user wrote, support owes an answer), pending (support answered —
 *  `reply` sets it — and the user's turn), answered (marked by hand), closed. «Ждёт пользователя»,
 *  not «Ждёт ответа»: next to the queue's «Ждёт» column (how long the *user* has waited, WP-25)
 *  «Ждёт ответа» read as the opposite of what it means. */
export const SUPPORT: Table = {
  open: { tone: 'warn', label: 'Открыто' },
  pending: { tone: 'neutral', label: 'Ждёт пользователя' },
  answered: { tone: 'ok', label: 'Отвечено' },
  closed: { tone: 'neutral', label: 'Закрыто' },
}

export const BROADCAST: Table = {
  draft: { tone: 'neutral', label: 'Черновик' },
  scheduled: { tone: 'warn', label: 'Запланирована' },
  running: { tone: 'live', label: 'Отправляется' },
  completed: { tone: 'ok', label: 'Завершена' },
  failed: { tone: 'danger', label: 'Ошибка' },
  cancelled: { tone: 'neutral', label: 'Отменена' },
  stopped: { tone: 'neutral', label: 'Остановлена' },
}

/** An operator's account: active or switched off. */
export const ADMIN_STATUS: Table = {
  active: { tone: 'ok', label: 'Активен' },
  disabled: { tone: 'neutral', label: 'Отключён' },
}

export function jobStatus(status: string): StatusView {
  return lookup(JOB, status)
}

export function paymentStatus(status: string): StatusView {
  return lookup(PAYMENT, status)
}

export function payoutStatus(status: string): StatusView {
  return lookup(PAYOUT, status)
}

export function earningStatus(status: string): StatusView {
  return lookup(EARNING, status)
}

export function supportStatus(status: string): StatusView {
  return lookup(SUPPORT, status)
}

export function broadcastStatus(status: string): StatusView {
  return lookup(BROADCAST, status)
}

export function adminStatus(active: boolean): StatusView {
  return lookup(ADMIN_STATUS, active ? 'active' : 'disabled')
}

/** Campaign statuses live with the acquisition helpers (lib/acquisition.ts). */
export { campaignStatus } from '@/lib/acquisition'
